import { DashboardsAPI } from '@/API/Dashboards'
import { AppDispatch } from '@/redux'
import { dashboardsActions } from '@/redux/slice/dashboardsSlice'
import { dashboardCreationActions } from '@/redux/slice/dashboardCreationSlice'
import { showNotification } from './notificationService'
import { TCSAPIResponse } from '@/types/types'
import { appActions } from '@/redux/slice/appSlice'

export const getDashboards = (
  token: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await DashboardsAPI.getAll(token)
  if (resp.data) {
    dispatch(dashboardsActions.setDashboards(resp.data))
  }
  if (resp.err) {
    dispatch(dashboardsActions.setError(resp.err))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const getDashboard = (
  token: string,
  dashboardId: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  dispatch(dashboardsActions.setSelectedDashboard(null))
  const resp = await DashboardsAPI.getById(token, dashboardId)
  if (resp.data) {
    dispatch(dashboardsActions.setSelectedDashboard(resp.data))
  }
  if (resp.err) {
    dispatch(dashboardsActions.setError(resp.err))
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const createDashboard = (
  token: string,
  name: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await DashboardsAPI.create(token, { name })
  if (resp.data) {
    dispatch(dashboardsActions.addDashboard(resp.data))
    await dispatch(getDashboards(token))
    showNotification(
      { data: { success: true } as TCSAPIResponse, err: undefined },
      'Dashboard created successfully',
      {}
    )
  }
  if (resp.err) {
    showNotification(
      { data: undefined, err: resp.err },
      '',
      {}
    )
  }
  dispatch(appActions.setLoading(false))
  return resp
}

export const renameDashboard = (
  token: string,
  dashboardId: string,
  name: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await DashboardsAPI.rename(token, dashboardId, { name })
  if (resp.data) {
    dispatch(dashboardsActions.updateDashboardName({ id: dashboardId, name: resp.data.name }))
    await dispatch(getDashboards(token))
    showNotification(
      { data: { success: true } as TCSAPIResponse, err: undefined },
      'Dashboard renamed successfully',
      {}
    )
  }
  if (resp.err) {
    showNotification(
      { data: undefined, err: resp.err },
      '',
      {}
    )
  }
  dispatch(appActions.setLoading(false))
  return resp
}

/**
 * renameDashboardCreationThunk — inline rename for the creation shell.
 *
 * Optimistically updates `dashboardCreationSlice.dashboardName` first (instant UI
 * response), then persists via `PATCH /v1/dashboards/:id`. On backend failure the
 * name is rolled back to `previousName` and an error toast is shown.
 *
 * Called by `DashboardCreationShell` when the user commits an inline edit.
 * Not to be confused with `renameDashboard` (used from the Canvas viewing-mode
 * header and the list-page ⋮ menu, which updates `dashboardsSlice`).
 */
export const renameDashboardCreationThunk = (
  token: string,
  dashboardId: string,
  name: string,
  previousName: string
) => async (dispatch: AppDispatch) => {
  // Optimistic update — instant name change in the header
  dispatch(dashboardCreationActions.renameDashboard({ name, manual: true }))

  const resp = await DashboardsAPI.rename(token, dashboardId, { name })

  if (resp.err) {
    // Rollback: revert to the name the user saw before editing
    dispatch(dashboardCreationActions.renameDashboard({ name: previousName, manual: true }))
    showNotification(
      { data: undefined, err: resp.err },
      '',
      { rename_failed: "Couldn't rename. Try again." }
    )
  }

  return resp
}

export const deleteDashboard = (
  token: string,
  dashboardId: string
) => async (dispatch: AppDispatch) => {
  dispatch(appActions.setLoading(true))
  const resp = await DashboardsAPI.delete(token, dashboardId)
  if (!resp.err) {
    dispatch(dashboardsActions.removeDashboard(dashboardId))
    await dispatch(getDashboards(token))
    showNotification(
      { data: { success: true } as TCSAPIResponse, err: undefined },
      'Dashboard deleted',
      {}
    )
  }
  if (resp.err) {
    showNotification(
      { data: undefined, err: resp.err },
      '',
      {}
    )
  }
  dispatch(appActions.setLoading(false))
  return resp
}
